import { config } from "../config";
import apiClient from "./apiClient";
import {
    ON_HAND_BOOKING_HISTORY_SUFFIX,
    ON_HAND_BOOKING_PREDICTIONS_SUFFIX,
    ON_HAND_SCENARIO_RESULTS_SUFFIX,
    buildOnHandQueryKey,
    buildOnHandScenarioIds,
} from "./scenarioCacheUtils";
import {
    buildExistingScenariosQueryKey,
    fetchExistingScenarios,
} from "./useFetchExistingScenarios";
import {fetchSavedScenarioSummaries, getSavedScenarioSummariesQueryKey, SAVED_SCENARIO_SUMMARY_STALE_TIME, SAVED_SCENARIOS_CACHE_TIME} from './useFetchSavedScenarios';
import {resolveScenarioAccount} from './scenarioCatalogUtils';
import {fetchScenarioCalculatedInputs, getScenarioCalculatedInputsQueryKey} from './useScenarioCalculatedInputs';

export const prefetchScenarioCatalog = (queryClient, accountContext) => {
    const {accountId, isRollup} = resolveScenarioAccount(accountContext);
    if (!accountId) return Promise.resolve();
    return queryClient.prefetchQuery({
        queryKey: isRollup ? buildExistingScenariosQueryKey(accountId) : getSavedScenarioSummariesQueryKey(accountId),
        queryFn: () => isRollup
            ? fetchExistingScenarios({accountIdOverride: accountId})
            : fetchSavedScenarioSummaries({accountIdOverride: accountId}),
        staleTime: SAVED_SCENARIO_SUMMARY_STALE_TIME,
        gcTime: SAVED_SCENARIOS_CACHE_TIME,
    });
};

const ROLLUP_COMPONENT_PREFETCH_TTL_MS = 5 * 60 * 1000;
const ROLLUP_COMPONENT_PREFETCH_WAIT_MS = 1500;
const rollupComponentPrefetchStore = new Map();

/**
 * Helper functions for prefetching scenario data
 * These functions are used by queryClient.prefetchQuery to load data in the background
 */

const buildAccountHeader = (accountIdOverride) => (
    accountIdOverride ? { 'X-QFlow-Account-Id': accountIdOverride } : undefined
);

export const fetchOnHandBookingHistory = async (scenarioIds, { accountIdOverride = null } = {}) => {
    const response = await apiClient.get(`${config.REACT_APP_BACKEND_HOST}/on-hand/booking-history`, {
        params: {
            scenario_ids: JSON.stringify(scenarioIds)
        },
        headers: buildAccountHeader(accountIdOverride),
    });
    return response.data;
};

export const fetchOnHandBookingPredictions = async (scenarioIds, { accountIdOverride = null } = {}) => {
    const response = await apiClient.get(`${config.REACT_APP_BACKEND_HOST}/on-hand/results`, {
        params: {
            scenario_ids: JSON.stringify(scenarioIds)
        },
        headers: buildAccountHeader(accountIdOverride),
    });
    return response.data;
};

export const fetchScenarioResults = async (scenarioIds, { accountIdOverride = null } = {}) => {
    const response = await apiClient.get(`${config.REACT_APP_BACKEND_HOST}/scenarios/results`, {
        params: {
            scenario_ids: JSON.stringify(scenarioIds)
        },
        headers: buildAccountHeader(accountIdOverride),
    });
    return response.data;
};

export const fetchArrWaterfallProjectionsSingle = async (
    segment,
    scenarioId,
    { accountIdOverride = null } = {}
) => {
    const response = await apiClient.get(
        `${config.REACT_APP_BACKEND_HOST}/board-stats/arr-waterfall-projections`,
        {
            params: {
                segment: segment || 'default',
                scenario_ids: JSON.stringify([scenarioId])
            },
            headers: buildAccountHeader(accountIdOverride),
        }
    );
    return { scenarioId, data: response.data };
};

/**
 * Prefetch data for a single scenario
 * @param {Object} queryClient - React Query client instance
 * @param {string} scenarioId - The scenario ID to prefetch
 * @param {string} customerId - The customer ID for cache keys
 * @param {Array<string>} segments - Array of segment IDs to prefetch (defaults to ['default'])
 */
export const prefetchScenarioData = async (queryClient, scenarioId, customerId, segments = ['default']) => {
    if (!scenarioId || !customerId) return;

    const scenarioIds = buildOnHandScenarioIds([scenarioId], []);

    // Prefetch booking history
    await queryClient.prefetchQuery({
        queryKey: buildOnHandQueryKey(customerId, ON_HAND_BOOKING_HISTORY_SUFFIX, scenarioIds),
        queryFn: () => fetchOnHandBookingHistory(scenarioIds, { accountIdOverride: customerId }),
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    // Prefetch booking predictions
    await queryClient.prefetchQuery({
        queryKey: buildOnHandQueryKey(customerId, ON_HAND_BOOKING_PREDICTIONS_SUFFIX, scenarioIds),
        queryFn: () => fetchOnHandBookingPredictions(scenarioIds, { accountIdOverride: customerId }),
        staleTime: 1000 * 60 * 5,
    });

    // Prefetch scenario results
    await queryClient.prefetchQuery({
        queryKey: buildOnHandQueryKey(customerId, ON_HAND_SCENARIO_RESULTS_SUFFIX, scenarioIds),
        queryFn: () => fetchScenarioResults(scenarioIds, { accountIdOverride: customerId }),
        staleTime: 1000 * 60 * 5,
    });

    // Prefetch ARR waterfall projections (for BoardStats) for all specified segments
    const segmentsToFetch = segments || ['default'];
    const arrWaterfallPrefetches = segmentsToFetch.map(segment =>
        queryClient.prefetchQuery({
            queryKey: [`${customerId}_board-stats-arr-waterfall-projections`, segment, scenarioId],
            queryFn: () => fetchArrWaterfallProjectionsSingle(
                segment,
                scenarioId,
                { accountIdOverride: customerId }
            ),
            staleTime: 1000 * 60 * 5, // 5 minutes - matches optimized hook
        })
    );
    await Promise.allSettled(arrWaterfallPrefetches);
};

const getRollupComponentPrefetchKey = (accountId, scenarioId) => `${accountId}:${scenarioId}`;

const isFreshRollupPrefetchEntry = (entry) => (
    entry && Date.now() - entry.createdAt < ROLLUP_COMPONENT_PREFETCH_TTL_MS
);

const setCachedQueryData = (queryClient, queryData = []) => {
    if (!queryClient || !Array.isArray(queryData)) return 0;
    queryData.forEach(({ queryKey, data }) => {
        if (queryKey && data !== undefined) {
            queryClient.setQueryData(queryKey, data);
        }
    });
    return queryData.length;
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Warm the exact child-account scenario queries used after drilling from a rollup composition.
 * This uses raw API fetches plus setQueryData because the global account switch intentionally
 * clears TanStack Query; the small module cache lets us seed the real query cache after that switch.
 */
export const prefetchRollupComponentScenarioData = (queryClient, { accountId, scenarioId } = {}) => {
    if (!accountId || !scenarioId) {
        return Promise.resolve(null);
    }

    const prefetchKey = getRollupComponentPrefetchKey(accountId, scenarioId);
    const existingEntry = rollupComponentPrefetchStore.get(prefetchKey);
    if (isFreshRollupPrefetchEntry(existingEntry)) {
        setCachedQueryData(queryClient, existingEntry.queryData);
        return existingEntry.promise || Promise.resolve(existingEntry);
    }

    const scenarioIds = buildOnHandScenarioIds([scenarioId], []);
    const querySpecs = [
        {
            name: "calculatedInputs",
            queryKey: getScenarioCalculatedInputsQueryKey(accountId),
            fetcher: () => fetchScenarioCalculatedInputs({accountIdOverride: accountId}),
        },
        {
            name: "bookingHistory",
            queryKey: buildOnHandQueryKey(accountId, ON_HAND_BOOKING_HISTORY_SUFFIX, scenarioIds),
            fetcher: () => fetchOnHandBookingHistory(scenarioIds, { accountIdOverride: accountId }),
        },
        {
            name: "bookingPredictions",
            queryKey: buildOnHandQueryKey(accountId, ON_HAND_BOOKING_PREDICTIONS_SUFFIX, scenarioIds),
            fetcher: () => fetchOnHandBookingPredictions(scenarioIds, { accountIdOverride: accountId }),
        },
        {
            name: "scenarioResults",
            queryKey: buildOnHandQueryKey(accountId, ON_HAND_SCENARIO_RESULTS_SUFFIX, scenarioIds),
            fetcher: () => fetchScenarioResults(scenarioIds, { accountIdOverride: accountId }),
        },
    ];

    const entry = {
        accountId,
        scenarioId,
        createdAt: Date.now(),
        queryData: [],
        errors: [],
        promise: null,
    };

    entry.promise = Promise.allSettled(
        querySpecs.map(async (spec) => ({
            name: spec.name,
            queryKey: spec.queryKey,
            data: await spec.fetcher(),
        }))
    ).then((results) => {
        entry.queryData = results
            .filter((result) => result.status === "fulfilled")
            .map((result) => ({
                queryKey: result.value.queryKey,
                data: result.value.data,
            }));
        entry.errors = results
            .filter((result) => result.status === "rejected")
            .map((result) => result.reason);
        setCachedQueryData(queryClient, entry.queryData);
        return entry;
    });

    rollupComponentPrefetchStore.set(prefetchKey, entry);
    return entry.promise;
};

export const seedRollupComponentScenarioData = async (
    queryClient,
    { accountId, scenarioId } = {},
    { waitForPendingMs = ROLLUP_COMPONENT_PREFETCH_WAIT_MS } = {}
) => {
    if (!accountId || !scenarioId) return false;

    const entry = rollupComponentPrefetchStore.get(getRollupComponentPrefetchKey(accountId, scenarioId));
    if (!isFreshRollupPrefetchEntry(entry)) return false;

    if ((!entry.queryData || entry.queryData.length === 0) && entry.promise && waitForPendingMs > 0) {
        await Promise.race([
            entry.promise.catch(() => null),
            wait(waitForPendingMs),
        ]);
    }

    return setCachedQueryData(queryClient, entry.queryData) > 0;
};

/**
 * Prefetch data for multiple scenarios (excluding the currently selected one)
 * @param {Object} queryClient - React Query client instance
 * @param {Array<string>} activeScenarios - Array of all active scenario IDs
 * @param {string} selectedScenarioId - The currently selected scenario ID
 * @param {string} customerId - The customer ID for cache keys
 * @param {Array<string>} segments - Array of segment IDs to prefetch (defaults to ['default'])
 */
export const prefetchOtherScenarios = async (queryClient, activeScenarios, selectedScenarioId, customerId, segments = ['default']) => {
    if (!activeScenarios?.length || !customerId) return;

    // Filter out the selected scenario (it's already loaded via hooks)
    const otherScenarios = activeScenarios.filter(id => id && id !== selectedScenarioId);

    // Prefetch each scenario's data in the background
    const prefetchPromises = otherScenarios.map(scenarioId =>
        prefetchScenarioData(queryClient, scenarioId, customerId, segments)
    );

    // Run all prefetches concurrently (non-blocking)
    await Promise.allSettled(prefetchPromises);
};
